import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  Grid,
  Typography,
} from "@mui/material";
import React, { useRef, useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Webcam from "react-webcam";
import { usePutOperationMutation } from "redux/api/operationApi";
import { setCurrentOperation } from "redux/features/operationSlice";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import { useIntl } from "react-intl";

const DocumentObverseCapture: React.FC<{
  nextStep: () => void;
  previousStep: () => void;
}> = ({ nextStep, previousStep }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const [
    updateOperation,
    {
      data: postedOperationData,
      isSuccess: isPostedOperationSuccess,
      error: apiError,
    },
  ] = usePutOperationMutation();
  const documentTypeName = useSelector(
      (state: any) => state.operationState?.documentTypeName
    );
  const IMAGE_SERVER = process.env.REACT_APP_IMAGES_ENDPOINT;
  const isLocalhost = (IMAGE_SERVER ?? "").includes("localhost");
  const [facingMode, setFacingMode] = useState<"user" | "environment">("environment");
  const videoConstraints = {
    facingMode: facingMode,
    video: { width: { ideal: 1920 }, height: { ideal: 1080 } },
  };

  const toggleCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };

  const webcamRef = useRef<Webcam>(null);
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [error, setError] = useState(false);

  const operation = useSelector(
    (state: any) =>
      state.operationState?.currentOperation?.data ||
      state.operationState?.currentOperation
  );

  useEffect(() => {
    if (operation?.documentObverse) {
      setImgSrc(`${IMAGE_SERVER}/${operation.documentObverse}`);
    }
  }, [operation]);

  const base64ToBlob = (base64: string, contentType: string): Blob => {
    const byteString = atob(base64.split(",")[1]);
    const mimeType =
      contentType ||
      base64.split(",")[0].match(/:(.*?);/)?.[1] ||
      "application/octet-stream";
    const arrayBuffer = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      arrayBuffer[i] = byteString.charCodeAt(i);
    }
    return new Blob([arrayBuffer], { type: mimeType });
  };

  const handleSubmit = () => {
    if (!imgSrc) {
      setError(true);
      return;
    }

    if (!(IMAGE_SERVER && imgSrc.startsWith(IMAGE_SERVER))) {
      const imageBlob = base64ToBlob(imgSrc, "image/png");
      const formData = new FormData();
      formData.append("documentObverse", imageBlob, `webcam-${Date.now()}.png`);
      const operationData = { ...operation, documentObverse: imgSrc };
      formData.append("data", JSON.stringify(operationData));
      
      updateOperation({ id: operation.id, operationToUpdate: formData });
    } else {
      nextStep();
    }
  };

  if (isPostedOperationSuccess && postedOperationData) {
    const operationData =
      "data" in postedOperationData
        ? postedOperationData.data // Si tiene `data`, accede a ella
        : postedOperationData;
    dispatch(setCurrentOperation(operationData));
    nextStep();
  }

  const capture = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setImgSrc(imageSrc);
    }
  }, [webcamRef]);

  const retakeImage = () => {
    setImgSrc(null);
    setError(false);
  };

  return (
    <Card sx={{ p: 2 }}>
      <CardHeader
        title={
          <Typography variant="h1" align="center">
            {formatMessage({ id: `document.obverse.title.${documentTypeName}` })}
          </Typography>
        }
      />
      <CardContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <FormControl error={error} fullWidth>
            {!imgSrc ? (
              <Box sx={{ position: "relative", width: "100%" }}>
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/png"
                  videoConstraints={videoConstraints}
                  style={{
                    width: "100%",
                    height: "auto",
                    aspectRatio: 3 / 4,
                    borderRadius: "10px",
                    border: "2px solid #ccc",
                  }}
                />
                <CameraswitchIcon
                  onClick={toggleCamera}
                  color="secondary"
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    minWidth: "40px",
                    minHeight: "40px",
                    borderRadius: "50%",
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <img
                  src={imgSrc}
                  alt="Captura de la webcam"
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: "10px",
                    marginTop: "10px",
                  }}
                />
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                mt: 2,
              }}
            >
              {!imgSrc ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={capture}
                  sx={{
                    width: "80%",
                    fontSize: 16,
                    padding: 1.25,
                  }}
                >
                  {formatMessage({ id: "buttons.take.picture" })}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={retakeImage}
                  sx={{
                    width: "80%",
                    fontSize: 16,
                    padding: 1.25,
                  }}
                >
                  {formatMessage({ id: "buttons.take.again" })}
                </Button>
              )}
            </Box>

            <Grid
              container
              spacing={2}
              justifyContent="space-between"
              sx={{ mt: 3 }}
            >
              <Grid item xs={12} lg={6}>
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  sx={{
                    mt: 2,
                    backgroundColor: "#FFF",
                    display: {
                      xs: "none", // Ocultar en pantallas pequeñas (móviles)
                      sm: "none", // Ocultar en tablets
                      md: "block", // Mostrar en pantallas medianas y grandes
                    },
                  }}
                  onClick={previousStep}
                >
                  {formatMessage({ id: "buttons.back" })}
                </Button>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 2,
                    backgroundColor: "black",
                    color: "white",
                    fontWeight: "bold",
                    "&:hover": {
                      backgroundColor: "#333",
                    },
                  }}
                  disabled={error || !imgSrc}
                >
                  {formatMessage({ id: "buttons.continue" })}
                </Button>
              </Grid>
            </Grid>
          </FormControl>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DocumentObverseCapture;
